import { Event, defined, RuntimeError, Cartesian3, VerticalOrigin, HeightReference, BoundingSphere, Ellipsoid, Color, PinBuilder, PrimitiveCollection, BillboardCollection, LabelCollection, PointPrimitiveCollection, BlendOption, CircleGeometry, GeometryInstance, ColorGeometryInstanceAttribute, PolygonGeometry, PolygonHierarchy, PerInstanceColorAppearance, ArcType, PolylineGeometry, PolylineColorAppearance, DeveloperError, Credit, Resource, getFilenameFromUri, BlendingState, Primitive } from 'cesium';
import { nanoid } from 'nanoid';
import centroid from '@turf/centroid';
import { polygon } from '@turf/helpers';

class BasicGraphicLayer {
  _changed;
  _subscriber;
  _subscribIds = [];
  constructor(options) {
    this._subscriber = options.subscriber;
    this._changed = new Event();
  }
  addSubscribers(callback, eventType) {
    if (!this._subscriber)
      return;
    const id = this._subscriber.addExternal(callback, eventType);
    if (id)
      this._subscribIds.push(id);
  }
  removeSubscribers(ids) {
    var _a;
    (_a = this._subscriber) == null ? void 0 : _a.removeExternal(ids ?? this._subscribIds);
  }
}

function defaultCrsFunction(coordinates) {
  return Cartesian3.fromDegrees(coordinates[0], coordinates[1], coordinates[2]);
}
const crsNames = {
  "urn:ogc:def:crs:OGC:1.3:CRS84": defaultCrsFunction,
  "EPSG:4326": defaultCrsFunction,
  "urn:ogc:def:crs:EPSG::4326": defaultCrsFunction
};
const crsLinkHrefs = {};
const crsLinkTypes = {};
function coordinatesArrayToCartesianArray(coordinates, crsFunction) {
  const positions = new Array(coordinates.length);
  coordinates.map((coor, i) => {
    positions[i] = crsFunction(coor);
  });
  return positions;
}
const geoJsonObjectTypes = {
  Feature: processFeature,
  FeatureCollection: processFeatureCollection,
  GeometryCollection: processGeometryCollection,
  LineString: processLineString,
  MultiLineString: processMultiLineString,
  MultiPoint: processMultiPoint,
  MultiPolygon: processMultiPolygon,
  Point: processPoint,
  Polygon: processPolygon
};
const geometryTypes = {
  GeometryCollection: processGeometryCollection,
  LineString: processLineString,
  MultiLineString: processMultiLineString,
  MultiPoint: processMultiPoint,
  MultiPolygon: processMultiPolygon,
  Point: processPoint,
  Polygon: processPolygon
};
function processFeature(geoJsonLayer, feature, notUsed, crsFunction, options) {
  if (feature.geometry === null) {
    return;
  }
  if (!defined(feature.geometry)) {
    throw new RuntimeError("feature.geometry is required.");
  }
  const geometryType = feature.geometry.type;
  const geometryHandler = geometryTypes[geometryType];
  if (!defined(geometryHandler)) {
    throw new RuntimeError(`Unknown geometry type: ${geometryType}`);
  }
  geometryHandler(
    geoJsonLayer,
    feature,
    feature.geometry,
    crsFunction,
    options
  );
}
function processFeatureCollection(geoJsonLayer, featureCollection, notUsed, crsFunction, options) {
  const features = featureCollection.features;
  features.map((feature) => {
    processFeature(geoJsonLayer, feature, void 0, crsFunction, options);
  });
}
function processGeometryCollection(geoJsonLayer, geoJson, geometryCollection, crsFunction, options) {
  const geometries = geometryCollection.geometries;
  geometries.map((geometry) => {
    const geometryType = geometry.type;
    const geometryHandler = geometryTypes[geometryType];
    if (!defined(geometryHandler)) {
      throw new RuntimeError(`Unknown geometry type: ${geometryType}`);
    }
    geometryHandler(geoJsonLayer, geoJson, geometry, crsFunction, options);
  });
}
function createPoint(geoJsonLayer, geoJson, crsFunction, coordinates, options) {
  const symbol = options.markerSymbol;
  const color = options.markerColor;
  const size = options.markerSize;
  const properties = geoJson.properties ?? {};
  geoJsonLayer.addPoint({
    type: "Point",
    position: crsFunction(coordinates),
    style: {
      color,
      pixelSize: size,
      outlineColor: options.stroke,
      outlineWidth: options.strokeWidth
    },
    properties
  });
  if (!symbol)
    return;
  let canvasOrPromise;
  if (symbol !== "" && defined(symbol)) {
    if (symbol.length === 1) {
      canvasOrPromise = geoJsonLayer.pinBuilder.fromText(
        symbol.toUpperCase(),
        color,
        size
      );
    } else {
      canvasOrPromise = geoJsonLayer.pinBuilder.fromMakiIconId(
        symbol,
        color,
        size
      );
    }
  } else {
    canvasOrPromise = geoJsonLayer.pinBuilder.fromColor(color, size);
  }
  const billboard = geoJsonLayer.addBillboard({
    type: "Billboard",
    position: crsFunction(coordinates),
    style: {
      verticalOrigin: VerticalOrigin.BOTTOM,
      heightReference: coordinates.length === 2 && options.clampToGround ? HeightReference.CLAMP_TO_GROUND : void 0
    },
    properties
  });
  const promise = Promise.resolve(canvasOrPromise).then(function(image) {
    billboard.image = image;
  }).catch(function() {
    billboard.image = geoJsonLayer.pinBuilder.fromColor(color, size);
  });
  geoJsonLayer._promises.push(promise);
}
function processPoint(geoJsonLayer, geoJson, geometry, crsFunction, options) {
  createPoint(
    geoJsonLayer,
    geoJson,
    crsFunction,
    geometry.coordinates,
    options
  );
}
function processMultiPoint(geoJsonLayer, geoJson, geometry, crsFunction, options) {
  geometry.coordinates.map(
    (coor) => createPoint(geoJsonLayer, geoJson, crsFunction, coor, options)
  );
}
function createLineString(geoJsonLayer, geoJson, crsFunction, coordinates, options) {
  const properties = geoJson.properties ?? {};
  const positions = coordinatesArrayToCartesianArray(coordinates, crsFunction);
  geoJsonLayer.addPolyline({
    type: "Polyline",
    positions,
    properties,
    style: {
      material: options.fill,
      width: options.strokeWidth
    }
  });
}
function processLineString(geoJsonLayer, geoJson, geometry, crsFunction, options) {
  createLineString(
    geoJsonLayer,
    geoJson,
    crsFunction,
    geometry.coordinates,
    options
  );
}
function processMultiLineString(geoJsonLayer, geoJson, geometry, crsFunction, options) {
  const lineStrings = geometry.coordinates;
  lineStrings.map((lineString) => {
    createLineString(geoJsonLayer, geoJson, crsFunction, lineString, options);
  });
}
function createPolygon(geoJsonLayer, geoJson, crsFunction, coordinates, options) {
  if (coordinates.length === 0 || coordinates[0].length === 0) {
    return;
  }
  const properties = geoJson.properties ?? {};
  const positions = coordinatesArrayToCartesianArray(
    coordinates[0],
    crsFunction
  );
  geoJsonLayer.addPolygon({
    type: "Polygon",
    positions,
    style: {
      material: options.fill,
      outlineColor: options.stroke,
      outlineWidth: options.strokeWidth
    },
    properties
  });
}
function processPolygon(geoJsonLayer, geoJson, geometry, crsFunction, options) {
  createPolygon(
    geoJsonLayer,
    geoJson,
    crsFunction,
    geometry.coordinates,
    options
  );
}
function processMultiPolygon(geoJsonLayer, geoJson, geometry, crsFunction, options) {
  const polygons = geometry.coordinates;
  polygons.map((polygon) => {
    createPolygon(geoJsonLayer, geoJson, crsFunction, polygon, options);
  });
}

function getPositionsCenter(positions, height) {
  const polygon$1 = polygon([positions.map((item) => [item.x, item.y])]);
  const polyCenter = BoundingSphere.fromPoints(positions).center;
  const polyCentroid = centroid(polygon$1);
  const center = new Cartesian3(
    polyCentroid.geometry.coordinates[0],
    polyCentroid.geometry.coordinates[1],
    polyCenter.z
  );
  return {
    cartesian3: Ellipsoid.WGS84.scaleToGeodeticSurface(center),
    height
  };
}

const DefaultColor = Color.fromCssColorString("#FC4C02");
const DefaultOptions = {
  markerSize: 10,
  markerColor: DefaultColor,
  stroke: Color.WHITE,
  strokeWidth: 2,
  fill: DefaultColor,
  clampToGround: false
};
class GeoJsonPrimitiveLayer extends BasicGraphicLayer {
  _name;
  _isLoading;
  _error;
  _loading;
  _primitiveCollection;
  _promises;
  _credit;
  _featureItems = [];
  _pinBuilder;
  _circleInstances = [];
  _polygonInstances = [];
  _polylineInstances = [];
  _billboardCollection;
  _labelCollection;
  _pointCollection;
  _circlePrimitive;
  _polygonPrimitive;
  _polylinePrimitive;
  _isDestroyed = false;
  _options;
  _geojson;
  constructor(options = {}) {
    super(options);
    this._error = new Event();
    this._isLoading = false;
    this._loading = new Event();
    this._pinBuilder = new PinBuilder();
    this._primitiveCollection = new PrimitiveCollection();
    this._billboardCollection = new BillboardCollection();
    this._labelCollection = new LabelCollection();
    this._pointCollection = new PointPrimitiveCollection({
      blendOption: BlendOption.TRANSLUCENT
    });
    this._options = { ...DefaultOptions, ...options.options };
    this._promises = [];
    this._credit = void 0;
  }
  get billboardCollection() {
    return this._billboardCollection;
  }
  get labelCollection() {
    return this._labelCollection;
  }
  get pointPrimitiveCollection() {
    return this._pointCollection;
  }
  get primitiveCollection() {
    return this._primitiveCollection;
  }
  get polygonPrimitive() {
    return this._polygonPrimitive;
  }
  get circlePrimitive() {
    return this._circlePrimitive;
  }
  get polylinePrimitive() {
    return this._polylinePrimitive;
  }
  get featureItems() {
    return this._featureItems;
  }
  get pinBuilder() {
    return this._pinBuilder;
  }
  /**
   * Gets or sets a human-readable name for this instance.
   * @type {String}
   */
  get name() {
    return this._name;
  }
  set name(value) {
    if (this._name !== value) {
      this._name = value;
      this._changed.raiseEvent(this);
    }
  }
  /**
   * Gets a value indicating if the data source is currently loading data.
   * @type {Boolean}
   */
  get loading() {
    return this._isLoading;
  }
  /**
   * Gets an event that will be raised when the underlying data changes.
   * @type {Event}
   */
  get changedEvent() {
    return this._changed;
  }
  get credit() {
    return this._credit;
  }
  /**
   * Gets an event that will be raised if an error is encountered during processing.
   * @type {Event}
   */
  get errorEvent() {
    return this._error;
  }
  /**
   * Gets an event that will be raised when the data source either starts or stops loading.
   * @type {Event}
   */
  get loadingEvent() {
    return this._loading;
  }
  /**
   * Gets whether or not this data source should be displayed.
   * @type {Boolean}
   */
  get show() {
    return this._primitiveCollection.show;
  }
  set show(value) {
    this._primitiveCollection.show = value;
  }
  /**
   * Gets an object that maps the name of a crs to a callback function which takes a GeoJSON coordinate
   * and transforms it into a WGS84 Earth-fixed Cartesian.  Older versions of GeoJSON which
   * supported the EPSG type can be added to this list as well, by specifying the complete EPSG name,
   * for example 'EPSG:4326'.
   * @type {Object}
   */
  get crsNames() {
    return crsNames;
  }
  /**
   * Gets an object that maps the href property of a crs link to a callback function
   * which takes the crs properties object and returns a Promise that resolves
   * to a function that takes a GeoJSON coordinate and transforms it into a WGS84 Earth-fixed Cartesian.
   * Items in this object take precedence over those defined in <code>crsLinkHrefs</code>, assuming
   * the link has a type specified.
   * @type {Object}
   */
  get crsLinkHrefs() {
    return crsLinkHrefs;
  }
  /**
   * Gets an object that maps the type property of a crs link to a callback function
   * which takes the crs properties object and returns a Promise that resolves
   * to a function that takes a GeoJSON coordinate and transforms it into a WGS84 Earth-fixed Cartesian.
   * Items in <code>crsLinkHrefs</code> take precedence over this object.
   * @type {Object}
   */
  get crsLinkTypes() {
    return crsLinkTypes;
  }
  get isDestroyed() {
    return this._isDestroyed;
  }
  get geojson() {
    return this._geojson;
  }
  _generateId() {
    return nanoid();
  }
  addBillboard(item) {
    const { position, style } = item;
    const id = this._generateId();
    const instance = this._billboardCollection.add({
      id,
      ...style,
      position
    });
    this.addFeatureItem({
      ...item,
      id,
      instance,
      center: { cartesian3: position }
    });
    return instance;
  }
  addLabel(item) {
    const { position, style } = item;
    const id = this._generateId();
    const instance = this._labelCollection.add({
      id,
      ...style,
      position
    });
    return instance;
  }
  addPoint(item) {
    const { position, style } = item;
    const id = this._generateId();
    const instance = this._pointCollection.add({
      id,
      position,
      ...style
    });
    this.addFeatureItem({
      ...item,
      id,
      instance,
      center: { cartesian3: position }
    });
    return instance;
  }
  addCircle(item) {
    const { position, style } = item;
    const id = this._generateId();
    const geometry = new CircleGeometry({
      center: position,
      extrudedHeight: style == null ? void 0 : style.extrudedHeight,
      radius: (style == null ? void 0 : style.radius) ?? 1e3
    });
    const instance = new GeometryInstance({
      geometry,
      attributes: {
        color: ColorGeometryInstanceAttribute.fromColor(
          (style == null ? void 0 : style.color) ?? this._options.fill
        )
      },
      id
    });
    this._circleInstances.push(instance);
    this.addFeatureItem({
      ...item,
      id,
      instance: geometry,
      center: { cartesian3: item.position }
    });
    return instance;
  }
  addPolygon(item) {
    const { positions, style } = item;
    const id = this._generateId();
    const geometry = new PolygonGeometry({
      polygonHierarchy: new PolygonHierarchy(positions),
      vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT,
      extrudedHeight: style == null ? void 0 : style.extrudedHeight,
      arcType: ArcType.RHUMB
    });
    const instance = new GeometryInstance({
      geometry,
      attributes: {
        color: ColorGeometryInstanceAttribute.fromColor(
          (style == null ? void 0 : style.material) ?? this._options.fill
        )
      },
      id
    });
    this._polygonInstances.push(instance);
    this.addFeatureItem({
      ...item,
      id,
      instance: geometry,
      center: getPositionsCenter(item.positions, style == null ? void 0 : style.extrudedHeight)
    });
    this.addPolyline(
      {
        type: "Polyline",
        positions,
        style: {
          width: style == null ? void 0 : style.outlineWidth,
          material: style == null ? void 0 : style.outlineColor
        }
      },
      false
    );
    return instance;
  }
  addPolyline(item, addFeature = true) {
    const { positions, style } = item;
    const id = this._generateId();
    const geometry = new PolylineGeometry({
      positions,
      vertexFormat: PolylineColorAppearance.VERTEX_FORMAT,
      width: style == null ? void 0 : style.width,
      arcType: ArcType.RHUMB
    });
    const instance = new GeometryInstance({
      geometry,
      attributes: {
        color: ColorGeometryInstanceAttribute.fromColor(
          (style == null ? void 0 : style.material) ?? this._options.stroke
        )
      },
      id
    });
    this._polylineInstances.push(instance);
    if (addFeature)
      this.addFeatureItem({
        ...item,
        id,
        instance: geometry,
        center: {
          cartesian3: item.positions[Math.floor(item.positions.length / 2)]
        }
      });
    return instance;
  }
  addFeatureItem(item) {
    this._featureItems.push(item);
    return item;
  }
  /**
   * 根据id获取FeatureItem
   * @param {string} id
   * @returns {PrimitiveItem | undefined} FeatureItem
   */
  getFeatureItemById(id) {
    return this._featureItems.find((item) => item.id === id);
  }
  /**
   * remove featureItem from primitive collection
   * @param {string} id featureItem id
   */
  removeFeatureItemById(id) {
    const feature = this._featureItems.find((item) => item.id === id);
    if (feature && feature.instance) {
      switch (feature.type) {
        case "Point":
          this._pointCollection.remove(feature.instance);
          break;
        case "Billboard":
          this._billboardCollection.remove(feature.instance);
          break;
        case "Label":
          this._labelCollection.remove(feature.instance);
          break;
      }
    }
    this._featureItems = this._featureItems.filter((item) => item.id !== id);
    return false;
  }
  /**
   * Asynchronously loads the provided GeoJSON or TopoJSON data, replacing any existing data.
   *
   * @param {Resource|String|Object} data A url, GeoJSON object to be loaded.
   * @param {Object} [options] An object with the following properties:
   * @param {String} [options.sourceUri] Overrides the url to use for resolving relative links.
   * @returns {Promise.<GeoJsonPrimitiveLayer>} a promise that will resolve when the GeoJSON is loaded.
   */
  static async load(url, options = {}) {
    let data = url;
    const layer = new GeoJsonPrimitiveLayer({ options });
    if (!defined(data)) {
      throw new DeveloperError("data is required.");
    }
    layer._isLoading = true;
    let credit = options.credit;
    if (typeof credit === "string") {
      credit = new Credit(credit);
    }
    layer._credit = credit;
    let promise = data;
    let sourceUri = options.sourceUri;
    if (typeof data === "string") {
      data = new Resource({ url: data });
    }
    if (data instanceof Resource) {
      promise = data.fetchJson();
      sourceUri = sourceUri ?? data.getUrlComponent();
    }
    try {
      const geoJson = await Promise.resolve(promise);
      layer._geojson = geoJson;
      await layer.preload(geoJson, options, sourceUri, true);
      return layer;
    } catch (error) {
      layer._isLoading = false;
      layer._error.raiseEvent(error);
      throw error;
    }
  }
  async preload(geoJson, layerOptions, sourceUri, clear) {
    const options = { ...this._options, ...layerOptions };
    let name;
    if (sourceUri) {
      name = getFilenameFromUri(sourceUri);
    }
    if (name && this._name !== name) {
      this._name = name;
      this._changed.raiseEvent(this);
    }
    const typeHandler = geoJsonObjectTypes[geoJson.type];
    if (!defined(typeHandler)) {
      throw new RuntimeError(
        `Unsupported GeoJSON object type: ${geoJson.type}`
      );
    }
    const crs = geoJson.crs;
    let crsFunction = crs !== null ? defaultCrsFunction : null;
    if (defined(crs)) {
      if (!defined(crs.properties)) {
        throw new RuntimeError("crs.properties is undefined.");
      }
      const properties = crs.properties;
      if (crs.type === "name") {
        crsFunction = crsNames[properties.name];
        if (!defined(crsFunction)) {
          throw new RuntimeError(`Unknown crs name: ${properties.name}`);
        }
      } else if (crs.type === "link") {
        let handler = crsLinkHrefs[properties.href];
        if (!defined(handler)) {
          handler = crsLinkTypes[properties.type];
        }
        if (!defined(handler)) {
          throw new RuntimeError(
            `Unable to resolve crs link: ${JSON.stringify(properties)}`
          );
        }
        crsFunction = handler(properties);
      } else if (crs.type === "EPSG") {
        crsFunction = crsNames[`EPSG:${properties.code}`];
        if (!defined(crsFunction)) {
          throw new RuntimeError(`Unknown crs EPSG code: ${properties.code}`);
        }
      } else {
        throw new RuntimeError(`Unknown crs type: ${crs.type}`);
      }
    }
    return Promise.resolve(crsFunction).then(async (crsFunc) => {
      if (clear) {
        this._primitiveCollection.removeAll();
      }
      if (crsFunc !== null) {
        typeHandler(
          this,
          geoJson,
          geoJson,
          crsFunc,
          options
        );
      }
      await Promise.all(this._promises);
      this._promises.length = 0;
      this._isLoading = false;
      this.reloadPrimitive();
      return this;
    });
  }
  removeAllPrimitive() {
    this.primitiveCollection.removeAll();
    this._billboardCollection = new BillboardCollection();
    this._labelCollection = new LabelCollection();
    this._pointCollection = new PointPrimitiveCollection({
      blendOption: BlendOption.TRANSLUCENT
    });
    this._featureItems = [];
    this._circleInstances = [];
    this._polygonInstances = [];
    this._polylineInstances = [];
  }
  reloadPrimitive(depthTest = this._options.depthTest ?? false) {
    const appearance = depthTest ? new PerInstanceColorAppearance({
      translucent: false,
      renderState: {
        depthTest: {
          enabled: true
        },
        depthMask: true,
        blending: BlendingState.PRE_MULTIPLIED_ALPHA_BLEND
      }
    }) : new PerInstanceColorAppearance({
      flat: true,
      translucent: false,
      closed: true,
      renderState: {
        depthTest: false,
        depthMask: false,
        blending: BlendingState.PRE_MULTIPLIED_ALPHA_BLEND
      }
    });
    this._circlePrimitive = new Primitive({
      geometryInstances: this._circleInstances,
      appearance,
      asynchronous: false
    });
    this._polygonPrimitive = new Primitive({
      geometryInstances: this._polygonInstances,
      appearance,
      asynchronous: false
    });
    this._polylinePrimitive = new Primitive({
      geometryInstances: this._polylineInstances,
      appearance: new PolylineColorAppearance({
        translucent: false
      }),
      asynchronous: false
    });
    this._primitiveCollection.add(this._circlePrimitive);
    this._primitiveCollection.add(this._polygonPrimitive);
    this._primitiveCollection.add(this._polylinePrimitive);
    this._primitiveCollection.add(this._billboardCollection);
    this._primitiveCollection.add(this._labelCollection);
    this._primitiveCollection.add(this._pointCollection);
  }
  destroy() {
    this.primitiveCollection.removeAll();
    this.removeSubscribers();
    this._featureItems = [];
    this._polygonInstances = [];
    this._polylineInstances = [];
    this._isDestroyed = true;
    return true;
  }
}

export { GeoJsonPrimitiveLayer, GeoJsonPrimitiveLayer as default, getPositionsCenter };
